import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BasketDrawerConnected } from '@common/components/BasketDrawer';
import { ContentParser } from '@common/components/ContentParser';

import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useLoadingState } from '@common/hooks';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import {
  useGetSpaFacility,
  useGetSpaFacilityTimeslots,
  useGetSpaSettings,
} from '@features/spa/hooks';
import { useTranslations } from '@lang';
import {
  getDiscountForMinimumGuests,
  getPriceForMinimumGuests,
} from '@utils/getPriceForMinimumGuests';
import { mergeCrossSellWithUpsell, parseDateToString } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import { useRouter } from 'shared-routing';

import { RecommendationItem, Recommendations, SpaTreatmentFacilityDetailTemplate } from 'voyager';

import { spaEntityFormSchema } from '@features/spa/pages/SpaTreatment/v2/SpaTreatmentForm.schema';
import { FacilityBookingV2 } from './FacilityBookingV2';
import { FacilityFormValues } from './FacilityBookingV2.types';
import { FacilityDetailV2Props } from './FacilityDetailV2.types';
import { FacilityExtrasV2 } from './FacilityExtrasV2';

export const FacilityDetailV2: FC<FacilityDetailV2Props> = ({
  id,
  bookingStep,
  property,
  language,
  journeyParams,
  userHasAccessToFacilities,
  loggedInGuest,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);

  const journeyBar = (
    <JourneyPickerV2Connected dateMode="single" dateContext="spa" showGuests numberOfMonths={1} />
  );

  const { data: spaEntity, isFetching } = useGetSpaFacility(
    property.id,
    language,
    id,
    parseDateToString(journeyParams.spaDate)
  );

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, !!spaEntity);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const termsAndConditions = spaSettings?.termsAndConditions || '';
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const {
    data: timeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaFacilityTimeslots(
    property.id,
    id,
    selectedDateString,
    language,
    !!spaEntity && userHasAccessToFacilities,
    1
  );

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;
  const isUnavailable =
    !isTelephoneOnly &&
    !isLoadingTimeslots &&
    (isTimeslotsError || (Array.isArray(timeslots) && timeslots.length === 0));

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  const minGuests = spaEntity?.minimumGuests || 1;
  const maxGuests = spaEntity?.maximumGuests || minGuests;
  const requestedGuests = journeyParams.adults || 1;
  const initialGuestCount = Math.max(minGuests, Math.min(maxGuests, requestedGuests));

  const form = useForm<FacilityFormValues>({
    resolver: zodResolver(spaEntityFormSchema),
    defaultValues: {
      date: moment(journeyParams.spaDate).format('YYYY-MM-DD'),
      guests: Array.from({ length: initialGuestCount }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender:
          spaEntity?.genderSpecific === 'M'
            ? 'male'
            : spaEntity?.genderSpecific === 'F'
            ? 'female'
            : '',
      })),
    },
    mode: 'onChange',
  });

  if (!userHasAccessToFacilities) {
    return (
      <SpaEntityError
        errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
        values={{ spaEntity: 'facilities' }}
      />
    );
  }

  const handleBack = () => {
    router.push({ pathname: Routes.FACILITY_LIST });
  };

  const handleReserve = () => {
    router.push({
      pathname: Routes.FACILITY_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  };

  const handleCloseModal = (showMiniBasket = false) => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
    if (showMiniBasket) {
      setIsBasketDrawerOpen(true);
    }
  };

  const mergedItems = mergeCrossSellWithUpsell(
    spaEntity?.upSell,
    'spa-facility',
    spaEntity?.crossSellObjects
  );

  const recommendations: RecommendationItem[] = (mergedItems || []).map((item: any) => ({
    id: String(item.id),
    name: item.name,
    imageUrl: item.image?.url,
    price:
      item.price?.amount != null
        ? { amount: item.price.amount, currencyCode: item.price.currencyCode }
        : undefined,
  }));

  const handleSelectRecommendation = (recId: string) => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id: recId } });
  };

  const price = spaEntity
    ? getPriceForMinimumGuests(spaEntity.price, spaEntity.minimumGuests)
    : undefined;

  const discount = mapDiscountToDisplayObject(
    getDiscountForMinimumGuests(spaEntity?.discount, spaEntity?.minimumGuests ?? null)
  );

  const promotionName = spaEntity?.discount?.name;

  const durationLabel = spaEntity?.displayDuration ? `${spaEntity.displayDuration} min` : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  return (
    <>
      {!bookingStep && (
        <>
          <Spinner showDelayMessage isActive={isRefetching} />
          <SpaTreatmentFacilityDetailTemplate
            imageUrl={spaEntity?.image?.url}
            imageAlt={spaEntity?.name}
            title={spaEntity?.name || ''}
            price={
              price?.amount != null
                ? { amount: price.amount, currencyCode: price.currencyCode }
                : undefined
            }
            discount={discount}
            priceLabel={t('common.from')}
            promotionName={promotionName}
            categoryLabel={durationLabel}
            description={spaEntity?.description}
            termsContent={
              spaEntity?.termsAndConditions || termsAndConditions ? (
                <ContentParser text={spaEntity?.termsAndConditions || termsAndConditions} />
              ) : undefined
            }
            termsLabel={t('app.termsAndConditions')}
            onReserve={
              spaEntity &&
              !isTelephoneOnly &&
              !isUnavailable &&
              !isRefetching &&
              !isLoadingTimeslots
                ? handleReserve
                : undefined
            }
            reserveLabel={t('spa.book')}
            onBack={handleBack}
            isLoading={isInitialLoading}
            journeyBar={journeyBar}
            telephoneOnly={
              isTelephoneOnly
                ? {
                    title: t('spa.telephoneOnly', {
                      spaEntity: t('spa.facility').toLowerCase(),
                    }),
                    subtitle: t('treatment.callUsOn', {
                      phoneNumber: spaSettings?.phoneNumber,
                    }),
                  }
                : undefined
            }
            unavailable={
              isUnavailable
                ? {
                    title: t('common.unavailable'),
                    subtitle: t('spa.package.unavailableDescription', {
                      title: spaEntity?.name,
                      date: moment(selectedDateString).format('dddd Do MMMM YYYY'),
                    }),
                    ctaLabel: t('common.backToModuleRoot', { entity: t('app.facilities') }),
                    onCtaClick: handleBack,
                  }
                : undefined
            }
            recommendations={
              recommendations.length > 0 ? (
                <Recommendations
                  items={recommendations}
                  onSelect={handleSelectRecommendation}
                  title={t('spa.headingColumnTitle')}
                  fromPriceLabel={t('common.from')}
                  noImageLabel={t('spa.noImage')}
                />
              ) : undefined
            }
          />
        </>
      )}
      {bookingStep === 'availability' && spaEntity && (
        <FormProvider {...form}>
          <FacilityBookingV2
            id={id}
            property={property}
            language={language}
            onClose={handleCloseModal}
            loggedInGuest={loggedInGuest}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && spaEntity && (
        <FormProvider {...form}>
          <FacilityExtrasV2
            spaEntity={spaEntity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.FACILITY_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerConnected
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
